import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, MenuItem, Skeleton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getRegularDate } from "utils/time.util";
import { itemTypes, resolveItemTypeKey } from "data/item-types";
import { formatStyleMap } from "features/WorkCited/data/fomat-citation-style";
import { mapCitationData } from "data/citation";
import { fetchCitationFields } from "features/Bookmarks/services/citation.service";

const NOODLETOOLS_ACCESS_KEY = process.env.REACT_APP_NOODLETOOLS_ACCESS_KEY;
const NOODLETOOLS_CLIENT_KEY = process.env.REACT_APP_NOODLETOOLS_CLIENT_KEY;

const RIS_TYPE_MAP = {
  blogpost: "BLOG",
  webpage: "ELEC",
  artwork: "ART",
  attachment: "GEN",
  audiorecording: "SOUND",
  bill: "BILL",
  book: "BOOK",
  booksection: "CHAP",
  case: "CASE",
  computerprogram: "COMP",
  conferencepaper: "CPAPER",
  dataset: "DATA",
  dictionaryentry: "DICT",
  document: "GEN",
  email: "PCOMM",
  encyclopediaarticle: "ENCYC",
  film: "MPCT",
  forumpost: "ICOMM",
  hearing: "HEAR",
  instantmessage: "ICOMM",
  interview: "GEN",
  journalarticle: "JOUR",
  letter: "PAMP",
  magazinearticle: "MGZN",
  manuscript: "MANSCPT",
  map: "MAP",
  newspaperarticle: "NEWS",
  patent: "PAT",
  podcast: "POD",
  preprint: "UNPB",
  presentation: "GEN",
  radiobroadcast: "SOUND",
  report: "RPRT",
  standard: "STAND",
  statute: "STAT",
  thesis: "THES",
  tvbroadcast: "MPCT",
  videorecording: "VIDEO",
};

function buildArchiveRISData(citation, mapped) {
  const lines = [];
  lines.push(`TY  - ${RIS_TYPE_MAP[String(citation.itemType_chr).toLowerCase()] || "GEN"}`);
  if (citation.title_chr) lines.push(`TI  - ${citation.title_chr}`);
  if (citation.author_chr) lines.push(`AU  - ${citation.author_chr}`);
  const { year } = parseArchiveDateParts(citation.date_dtm);
  if (year) {
    lines.push(`PY  - ${year}`);
    lines.push(`DA  - ${citation.date_dtm}`);
  }
  if (mapped.publicationTitle) {
    lines.push(`T2  - ${mapped.publicationTitle}`);
    lines.push(`JO  - ${mapped.publicationTitle}`);
  }
  if (citation.pages_chr) lines.push(`SP  - ${citation.pages_chr}`);
  if (citation.edition_chr) lines.push(`ET  - ${citation.edition_chr}`);
  if (citation.publisher_chr) lines.push(`PB  - ${citation.publisher_chr}`);
  if (citation.url_chr) lines.push(`UR  - ${citation.url_chr}`);
  if (citation.accessDate_dtm) {
    const accessDate = new Date(citation.accessDate_dtm);
    if (!Number.isNaN(accessDate.getTime())) {
      const y = accessDate.getFullYear();
      const m = String(accessDate.getMonth() + 1).padStart(2, "0");
      const d = String(accessDate.getDate()).padStart(2, "0");
      lines.push(`Y2  - ${y}/${m}/${d}`);
    }
  }
  if (citation.isbN_chr) lines.push(`SN  - ${citation.isbN_chr}`);
  if (citation.doI_chr) lines.push(`DO  - ${citation.doI_chr}`);
  lines.push("ER  - ");
  return lines.join("\n");
}

const itemTypeLabel = (itemType) => {
  if (!itemType) return null;
  const resolvedKey = resolveItemTypeKey(itemType, itemTypes);
  return itemTypes.find((type) => type.key === resolvedKey)?.name || itemType;
};

const FALLBACK_FIELDS_BEFORE_DATE = [
  { name: "creators", label: "Author(s)" },
  { name: "title", label: "Title" },
  { name: "abstractNote", label: "Abstract Note" },
  { name: "publicationTitle", label: "Publication / Website Title" },
];

const FALLBACK_FIELDS_AFTER_DATE = [
  { name: "publisher", label: "Publisher" },
  { name: "pages", label: "Pages" },
  { name: "edition", label: "Edition" },
  { name: "ISBN", label: "ISBN" },
  { name: "DOI", label: "DOI" },
  { name: "url", label: "URL" },
  { name: "accessDate", label: "Access Date", isDate: true },
];

const parseArchiveDateParts = (value) => {
  if (!value) return { day: "", month: "", year: "" };
  const parts = String(value).trim().split(/\s+/);
  if (parts.length >= 3) return { day: parts[0], month: parts[1], year: parts[2] };
  if (parts.length === 2) return { day: "", month: parts[0], year: parts[1] };
  return { day: "", month: "", year: parts[0] || "" };
};

const citationFieldsCache = new Map();

function getCachedCitationFields(user, itemType, style) {
  const cacheKey = `${itemType.key}|${style}`;
  if (!citationFieldsCache.has(cacheKey)) {
    citationFieldsCache.set(
      cacheKey,
      fetchCitationFields(user, itemType, style).catch((error) => {
        citationFieldsCache.delete(cacheKey);
        throw error;
      })
    );
  }
  return citationFieldsCache.get(cacheKey);
}

function fieldValue(mapped, source, fieldName, baseFieldName) {
  if (fieldName === "abstractNote") return mapped.abstractNote || source?.summary_chr || "";
  if (mapped[fieldName]) return mapped[fieldName];
  if (baseFieldName && mapped[baseFieldName]) return mapped[baseFieldName];
  return "";
}

function fieldSx(required, softRequired) {
  return {
    mb: 2,
    ...(softRequired
      ? {
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#f57c00" },
          },
          "& .MuiInputLabel-root": { color: "#f57c00" },
        }
      : {}),
  };
}

function buildRequiredFieldNames(fieldMeta) {
  const required = [];
  const soft = [];
  (fieldMeta?.dynamicFields || []).forEach((field) => {
    if (field.isSoftRequired_ysn) {
      soft.push(field.fieldName_chr);
    } else if (field.isRequired_ysn) {
      // A required field with a baseFieldName_chr (e.g. websiteTitle -> publicationTitle)
      // is an item-type-specific alias of a generic base column. GetArchiveSnapshotDetail
      // only ever returns the base column (publicationTitle_chr) in citations[] — it has
      // no websiteTitle_chr/blogTitle_chr/etc. of its own, even for a citation that was
      // generated with a complete website/blog/forum title. That's a backend gap in what
      // the archive snapshot persists, not something this mapping can recover — downgrade
      // to a soft warning instead of hard-blocking, since we can't tell "the archived
      // record never captured this" from "the student's citation was genuinely
      // incomplete." Non-required aliased fields (e.g. websiteType) are left alone.
      if (field.baseFieldName_chr) {
        soft.push(field.fieldName_chr);
      } else {
        required.push(field.fieldName_chr);
      }
    }
  });
  (fieldMeta?.creators || []).forEach((creator) => {
    if (!creator.isDefault_ysn) return;
    const key = creator.creatorName_chr === "author" ? "creators" : creator.creatorName_chr;
    if (creator.isSoftRequired_ysn) soft.push(key);
    else if (creator.isRequired_ysn) required.push(key);
  });
  return { required, soft };
}

export default function SnapshotCitationView({ citation, source }) {
  const { user } = useSelector((state) => state.auth);
  const [fieldMeta, setFieldMeta] = useState(null);
  const [fieldMetaLoading, setFieldMetaLoading] = useState(true);
  const [copyTooltip, setCopyTooltip] = useState("Copy");
  const noodleToolsFormRef = useRef();

  useEffect(() => {
    setFieldMeta(null);
    setFieldMetaLoading(true);
    if (!citation?.itemType_chr || !user) {
      setFieldMetaLoading(false);
      return;
    }
    const resolvedKey = resolveItemTypeKey(citation.itemType_chr, itemTypes);
    const itemType = itemTypes.find((type) => type.key === resolvedKey);
    if (!itemType) {
      setFieldMetaLoading(false);
      return;
    }
    let cancelled = false;
    getCachedCitationFields(user, itemType, citation.styles_chr || "apa")
      .then((data) => {
        if (!cancelled) setFieldMeta(data?.[0] || { dynamicFields: [], creators: [] });
      })
      .catch(() => {
        if (!cancelled) setFieldMeta(null);
      })
      .finally(() => {
        if (!cancelled) setFieldMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [citation?.itemType_chr, citation?.styles_chr, user]);

  if (!citation) {
    return (
      <Typography color="grey.600">
        This student hasn't generated a citation for this bookmark.
      </Typography>
    );
  }

  const handleCopyCitation = () => {
    const textToCopy = citation.citation_chr || "";
    const html = new Blob([textToCopy], { type: "text/html" });
    const plain = new Blob([textToCopy.replace(/<[^>]*>?/gm, "")], { type: "text/plain" });
    navigator.clipboard
      .write([new ClipboardItem({ "text/html": html, "text/plain": plain })])
      .then(() => setCopyTooltip("Copied!"))
      .catch(() => {});
  };

  const handleExportToNoodleTools = (mapped) => {
    const risString = buildArchiveRISData(citation, mapped);
    if (noodleToolsFormRef.current) {
      const textarea = noodleToolsFormRef.current.querySelector("textarea[name='risdatabyformpost']");
      if (textarea) textarea.value = risString;
      noodleToolsFormRef.current.submit();
    }
  };

  const mapped = mapCitationData(citation);
  const findField = (name) => fieldMeta?.dynamicFields?.find((field) => field.fieldName_chr === name);
  const labelFor = (name) => (name === "creators" ? "Author(s)" : findField(name)?.displayFieldName_chr || name);
  const isMissing = (name) =>
    name === "creators"
      ? !citation.author_chr
      : !fieldValue(mapped, source, name, findField(name)?.baseFieldName_chr);
  const { required, soft } = buildRequiredFieldNames(fieldMeta);
  const missingHard = fieldMeta ? required.filter(isMissing) : [];
  const missingSoft = fieldMeta ? soft.filter(isMissing) : [];

  const fieldRows = fieldMeta
    ? fieldMeta.dynamicFields
        .filter((field) => field.fieldName_chr !== "date")
        .map((field) => ({
          name: field.fieldName_chr,
          label: field.displayFieldName_chr,
          baseFieldName: field.baseFieldName_chr,
        }))
    : [...FALLBACK_FIELDS_BEFORE_DATE, ...FALLBACK_FIELDS_AFTER_DATE].filter((field) => field.name !== "creators");
  const hasDateField = fieldMeta ? fieldMeta.dynamicFields.some((field) => field.fieldName_chr === "date") : true;
  const showAuthors = fieldMeta ? fieldMeta.creators?.length > 0 : true;

  if (fieldMetaLoading) {
    return (
      <Stack gap={2}>
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 0.5 }} />
        <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={56} sx={{ mb: 2 }} />
        ))}
      </Stack>
    );
  }

  return (
    <React.Fragment>
      {missingHard.length === 0 && (
        <React.Fragment>
          <Typography sx={{ mb: 0.5 }} variant="body3" color="grey.600">
            {`Generated Citation${
              formatStyleMap[citation.styles_chr] || citation.styles_chr
                ? ` (${formatStyleMap[citation.styles_chr] || citation.styles_chr})`
                : ""
            }`}
          </Typography>
          <Typography
            variant="subtitle2"
            component="div"
            dangerouslySetInnerHTML={{ __html: citation.citation_chr || "" }}
            sx={{
              maxHeight: "15vh",
              overflowY: "auto",
              overflowX: "hidden",
              p: 1.5,
              mb: missingSoft.length ? 1.5 : 2,
              border: missingSoft.length > 0 ? "1px solid" : "none",
              borderColor: "#f57c00",
              borderRadius: "4px",
              backgroundColor: missingSoft.length > 0 ? "rgba(245, 124, 0, 0.05)" : "transparent",
              color: missingSoft.length > 0 ? "#f57c00" : "inherit",
            }}
          />
        </React.Fragment>
      )}
      {missingHard.length > 0 ? (
        <Box
          p={1.5}
          mb={2}
          sx={{
            backgroundColor: "rgba(211, 47, 47, 0.1)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "start",
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "error.main", fontWeight: 600, flex: 1 }}>
            Your citation is missing required information. Please fill in all required fields.
          </Typography>
          <Tooltip
            arrow
            placement="bottom-start"
            title={
              <Box p={1}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#fff" }}>
                  Missing Required Info:
                </Typography>
                <Stack gap={0.5}>
                  {missingHard.map((field) => (
                    <Typography key={field} variant="caption" sx={{ display: "list-item", ml: 2, color: "#fff" }}>
                      {labelFor(field)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ color: "error.main", fontSize: "16px", cursor: "pointer", mt: 0.3 }} />
          </Tooltip>
        </Box>
      ) : missingSoft.length > 0 ? (
        <Box
          p={1.5}
          mb={2}
          sx={{
            backgroundColor: "rgba(245, 124, 0, 0.1)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "start",
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: "#f57c00", fontWeight: 500, flex: 1 }}>
            Wait! Your citation might not be complete. It is missing information that is{" "}
            <i>typically</i> included. Please double-check your source document and provide
            the missing info, if it is available.
          </Typography>
          <Tooltip
            arrow
            placement="bottom-start"
            title={
              <Box p={1}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#fff" }}>
                  Missing Recommended Info:
                </Typography>
                <Stack gap={0.5}>
                  {missingSoft.map((field) => (
                    <Typography key={field} variant="caption" sx={{ display: "list-item", ml: 2, color: "#fff" }}>
                      {labelFor(field)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ color: "#f57c00", fontSize: "16px", cursor: "pointer", mt: 0.3 }} />
          </Tooltip>
        </Box>
      ) : null}
      {missingHard.length === 0 && (
        <React.Fragment>
          <Stack direction="row" gap={2} mb={NOODLETOOLS_CLIENT_KEY ? 1 : 2}>
            <Tooltip title={copyTooltip} arrow>
              <Button
                startIcon={<ContentCopyIcon />}
                sx={{ px: 2, py: 1 }}
                variant="outlined"
                onClick={handleCopyCitation}
                onMouseLeave={() => setCopyTooltip("Copy")}
              >
                Copy
              </Button>
            </Tooltip>
            {NOODLETOOLS_CLIENT_KEY && (
              <form
                ref={noodleToolsFormRef}
                action="https://my.noodletools.com/import/rest-post.php"
                target="_blank"
                method="POST"
              >
                <input type="hidden" name="accesskey" value={NOODLETOOLS_ACCESS_KEY} />
                <input type="hidden" name="clientkey" value={NOODLETOOLS_CLIENT_KEY} />
                <input type="hidden" name="version" value="1.0" />
                <input type="hidden" name="datatype" value="ris" />
                <textarea name="risdatabyformpost" defaultValue="" readOnly style={{ display: "none" }} />
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => handleExportToNoodleTools(mapped)}
                  sx={{ px: 2, py: 1, textTransform: "none", fontWeight: 600 }}
                >
                  Export to NoodleTools
                </Button>
              </form>
            )}
          </Stack>
          {NOODLETOOLS_CLIENT_KEY && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Please ensure your citation is correct before you save, copy, or export it.
            </Typography>
          )}
        </React.Fragment>
      )}
      <Stack gap={2}>
        <TextField select label="Style Type" value={citation.styles_chr || ""} fullWidth disabled sx={{ mb: 2 }}>
            <MenuItem value={citation.styles_chr || ""}>
              {formatStyleMap[citation.styles_chr] || citation.styles_chr}
            </MenuItem>
          </TextField>
          <TextField select label="Item Type" value={citation.itemType_chr || ""} fullWidth disabled sx={{ mb: 2 }}>
            <MenuItem value={citation.itemType_chr || ""}>{itemTypeLabel(citation.itemType_chr)}</MenuItem>
          </TextField>
          {showAuthors &&
            (() => {
              const authorRequired = missingHard.includes("creators");
              const authorSoftRequired = !authorRequired && missingSoft.includes("creators");
              return (
                <TextField
                  label="Author(s)"
                  value={citation.author_chr || ""}
                  fullWidth
                  disabled
                  error={authorRequired}
                  helperText={authorRequired ? "This field is required" : ""}
                  sx={fieldSx(authorRequired, authorSoftRequired)}
                />
              );
            })()}
          {fieldRows.map(({ name, label, baseFieldName, isDate }) => {
            const rawValue = fieldValue(mapped, source, name, baseFieldName);
            const required = missingHard.includes(name);
            const softRequired = !required && missingSoft.includes(name);
            return (
              <TextField
                key={name}
                label={label}
                value={rawValue ? (isDate ? getRegularDate(rawValue) : rawValue) : ""}
                fullWidth
                disabled
                error={required}
                helperText={required ? "This field is required" : ""}
                multiline={name === "abstractNote"}
                minRows={name === "abstractNote" ? 3 : 1}
                sx={fieldSx(required, softRequired)}
              />
            );
          })}
          {hasDateField &&
            (() => {
              const { day, month, year } = parseArchiveDateParts(citation.date_dtm);
              return (
                <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                  <TextField label="Day (DD)" value={day} fullWidth disabled />
                  <TextField label="Month (MM)" value={month} fullWidth disabled />
                  <TextField label="Year (YYYY)" value={year} fullWidth disabled />
                </Stack>
              );
            })()}
      </Stack>
    </React.Fragment>
  );
}
